import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Target, Zap, Calendar, Award, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { WebLayout } from '../components/WebLayout';
import { useUser } from '../contexts/UserContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { LimitReachedModal } from '../components/LimitReachedModal';
interface AnalyticsData {
  totalQuests: number;
  completedQuests: number;
  completionRate: number;
  averageCompletionTime: number;
  mostProductiveDay: string;
  categoryBreakdown: {
    category: string;
    count: number;
    percentage: number;
  }[];
  weeklyProgress: {
    day: string;
    completed: number;
  }[];
  monthlyTrend: {
    month: string;
    completed: number;
  }[];
  focusTimeTotal: number;
  currentStreak: number;
  longestStreak: number;
}
export function AnalyticsPage() {
  const navigate = useNavigate();
  const {
    stats,
    goals
  } = useUser();
  const {
    tier
  } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (tier === 'free') {
      setShowUpgradeModal(true);
      return;
    }
    // Calculate analytics from goals
    const totalQuests = goals.length;
    const completedQuests = goals.filter(g => g.completed).length;
    const completionRate = totalQuests > 0 ? completedQuests / totalQuests * 100 : 0;
    // Category breakdown
    const categoryCount: Record<string, number> = {};
    goals.forEach(goal => {
      categoryCount[goal.category] = (categoryCount[goal.category] || 0) + 1;
    });
    const categoryBreakdown = Object.entries(categoryCount).map(([category, count]) => ({
      category,
      count,
      percentage: count / totalQuests * 100
    }));
    // Weekly progress (mock data - would come from backend)
    const weeklyProgress = [{
      day: 'Mon',
      completed: 3
    }, {
      day: 'Tue',
      completed: 5
    }, {
      day: 'Wed',
      completed: 4
    }, {
      day: 'Thu',
      completed: 6
    }, {
      day: 'Fri',
      completed: 7
    }, {
      day: 'Sat',
      completed: 2
    }, {
      day: 'Sun',
      completed: 4
    }];
    // Monthly trend (mock data - would come from backend)
    const monthlyTrend = [{
      month: 'Jan',
      completed: 45
    }, {
      month: 'Feb',
      completed: 52
    }, {
      month: 'Mar',
      completed: 48
    }, {
      month: 'Apr',
      completed: 61
    }, {
      month: 'May',
      completed: 58
    }, {
      month: 'Jun',
      completed: 67
    }];
    setAnalytics({
      totalQuests,
      completedQuests,
      completionRate,
      averageCompletionTime: 2.5,
      mostProductiveDay: 'Friday',
      categoryBreakdown,
      weeklyProgress,
      monthlyTrend,
      focusTimeTotal: stats.totalFocusMinutes || 0,
      currentStreak: stats.streak,
      longestStreak: stats.streak // Would track this separately
    });
    setLoading(false);
  }, [goals, stats, tier]);
  if (tier === 'free') {
    return <>
        <WebLayout data-id="element-607">
          <div className="min-h-screen bg-game-bg-pink dark:bg-[#1a1d29] flex items-center justify-center p-6" data-id="element-608">
            <Card className="max-w-md w-full p-8 text-center" data-id="element-609">
              <div className="w-20 h-20 bg-gradient-to-br from-game-yellow to-game-coral rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-610">
                <BarChart3 className="w-10 h-10 text-white" data-id="element-611" />
              </div>
              <h2 className="text-2xl font-fredoka font-bold text-game-dark dark:text-white mb-4" data-id="element-612">
                Advanced Analytics
              </h2>
              <p className="text-gray-600 dark:text-gray-400 font-quicksand mb-6" data-id="element-613">
                Get detailed insights into your productivity patterns,
                completion rates, and progress trends with Premium.
              </p>
              <Button onClick={() => navigate('/upgrade')} className="w-full bg-gradient-to-r from-game-purple to-game-teal" data-id="element-614">
                Upgrade to Premium
              </Button>
            </Card>
          </div>
        </WebLayout>
        <LimitReachedModal isOpen={showUpgradeModal} onClose={() => {
        setShowUpgradeModal(false);
        navigate(-1);
      }} onUpgrade={() => {
        setShowUpgradeModal(false);
        navigate('/upgrade');
      }} limitType="export" data-id="element-615" />
      </>;
  }
  if (loading || !analytics) {
    return <WebLayout data-id="element-616">
        <div className="flex items-center justify-center min-h-screen" data-id="element-617">
          <div className="text-center" data-id="element-618">
            <div className="w-16 h-16 border-4 border-game-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-619"></div>
            <p className="font-quicksand text-gray-500 dark:text-gray-400" data-id="element-620">
              Loading analytics...
            </p>
          </div>
        </div>
      </WebLayout>;
  }
  return <WebLayout data-id="element-621">
      <div className="min-h-screen bg-game-bg-pink dark:bg-[#1a1d29] pb-24" data-id="element-622">
        <header className="px-6 pt-6 pb-4 sticky top-0 bg-game-bg-pink/90 dark:bg-[#1a1d29]/90 backdrop-blur-sm z-10" data-id="element-623">
          <div className="flex items-center gap-4" data-id="element-624">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="!p-2" data-id="element-625">
              <ArrowLeft className="w-6 h-6" data-id="element-626" />
            </Button>
            <div data-id="element-627">
              <h1 className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-628">
                Analytics
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-629">
                Your productivity insights
              </p>
            </div>
          </div>
        </header>

        <main className="px-6 space-y-6 max-w-6xl mx-auto" data-id="element-630">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-631">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} data-id="element-632">
              <Card className="p-4" data-id="element-633">
                <div className="flex items-center gap-3 mb-2" data-id="element-634">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center" data-id="element-635">
                    <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-636" />
                  </div>
                  <div data-id="element-637">
                    <p className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-638">
                      {analytics.completionRate.toFixed(0)}%
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-639">
                      Completion Rate
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-640">
              <Card className="p-4" data-id="element-641">
                <div className="flex items-center gap-3 mb-2" data-id="element-642">
                  <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center" data-id="element-643">
                    <TrendingUp className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-644" />
                  </div>
                  <div data-id="element-645">
                    <p className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-646">
                      {analytics.currentStreak}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-647">
                      Day Streak
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-648">
              <Card className="p-4" data-id="element-649">
                <div className="flex items-center gap-3 mb-2" data-id="element-650">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center" data-id="element-651">
                    <Zap className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-652" />
                  </div>
                  <div data-id="element-653">
                    <p className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-654">
                      {Math.floor(analytics.focusTimeTotal / 60)}h
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-655">
                      Focus Time
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-656">
              <Card className="p-4" data-id="element-657">
                <div className="flex items-center gap-3 mb-2" data-id="element-658">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center" data-id="element-659">
                    <Award className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-660" />
                  </div>
                  <div data-id="element-661">
                    <p className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-662">
                      {analytics.completedQuests}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-663">
                      Completed
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Category Breakdown */}
          <Card className="p-6" data-id="element-664">
            <h3 className="font-fredoka font-bold text-xl text-game-dark dark:text-white mb-4" data-id="element-665">
              Quest Categories
            </h3>
            <div className="space-y-4" data-id="element-666">
              {analytics.categoryBreakdown.map((cat, index) => <motion.div key={cat.category} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} data-id="element-667">
                  <div className="flex items-center justify-between mb-2" data-id="element-668">
                    <span className="font-quicksand text-gray-700 dark:text-gray-300" data-id="element-669">
                      {cat.category}
                    </span>
                    <span className="font-fredoka font-bold text-game-purple" data-id="element-670">
                      {cat.count} ({cat.percentage.toFixed(0)}%)
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-671">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${cat.percentage}%`
                }} transition={{
                  delay: index * 0.1 + 0.3,
                  duration: 0.5
                }} className="h-full bg-gradient-to-r from-game-purple to-game-teal" data-id="element-672" />
                  </div>
                </motion.div>)}
            </div>
          </Card>

          {/* Weekly Progress */}
          <Card className="p-6" data-id="element-673">
            <h3 className="font-fredoka font-bold text-xl text-game-dark dark:text-white mb-4" data-id="element-674">
              This Week's Progress
            </h3>
            <div className="flex items-end justify-between gap-2 h-48" data-id="element-675">
              {analytics.weeklyProgress.map((day, index) => {
              const maxCompleted = Math.max(...analytics.weeklyProgress.map(d => d.completed));
              const height = day.completed / maxCompleted * 100;
              return <motion.div key={day.day} initial={{
                height: 0
              }} animate={{
                height: `${height}%`
              }} transition={{
                delay: index * 0.1,
                duration: 0.5
              }} className="flex-1 bg-gradient-to-t from-game-purple to-game-teal rounded-t-lg min-h-[20px] relative group" data-id="element-676">
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-677">
                      <span className="font-fredoka font-bold text-sm text-game-purple" data-id="element-678">
                        {day.completed}
                      </span>
                    </div>
                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-quicksand text-gray-500 dark:text-gray-400" data-id="element-679">
                      {day.day}
                    </div>
                  </motion.div>;
            })}
            </div>
          </Card>

          {/* Insights */}
          <Card className="p-6" data-id="element-680">
            <h3 className="font-fredoka font-bold text-xl text-game-dark dark:text-white mb-4" data-id="element-681">
              Insights
            </h3>
            <div className="space-y-3" data-id="element-682">
              <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl" data-id="element-683">
                <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" data-id="element-684" />
                <div data-id="element-685">
                  <p className="font-fredoka font-bold text-sm text-blue-900 dark:text-blue-100" data-id="element-686">
                    Most Productive Day
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300 font-quicksand" data-id="element-687">
                    You complete the most quests on{' '}
                    {analytics.mostProductiveDay}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl" data-id="element-688">
                <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" data-id="element-689" />
                <div data-id="element-690">
                  <p className="font-fredoka font-bold text-sm text-green-900 dark:text-green-100" data-id="element-691">
                    Average Completion Time
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300 font-quicksand" data-id="element-692">
                    You typically complete quests in{' '}
                    {analytics.averageCompletionTime} days
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </main>
      </div>
    </WebLayout>;
}